import CloseIcon from '@mui/icons-material/Close';
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { useDriveTimeStore } from "..";
import { useConditionallyClosable } from "../hooks/useClosable";
import useIsHalfScreenMap from "../hooks/useIsHalfScreenMap";
import { ClosableOrder } from "../store/ClosableStore";
import { DriveTimeMinutes } from "../store/DriveTimeStore";
import commonStyles from './Common.module.css';
import styles from './DriveTime.module.css';
import SegmentedButtons from "./SegmentedButtons";
import Spinner from "./spinners/Spinner";

function DriveTime() {
    const isHalfScreenMap = useIsHalfScreenMap();
    const driveTimeStore = useDriveTimeStore();
    const driveTimeSegmentedButtons = useMemo(() => computed(() => {
        const isBusy = (minutes: DriveTimeMinutes) => driveTimeStore.minutes === minutes
            && (driveTimeStore.isLoading || driveTimeStore.isIndustrialScarcityLoading);
        return [
            { value: DriveTimeMinutes._15, label: isBusy(DriveTimeMinutes._15) ? <Spinner /> : "15 min" },
            { value: DriveTimeMinutes._30, label: isBusy(DriveTimeMinutes._30) ? <Spinner /> : "30 min" },
            { value: DriveTimeMinutes._45, label: isBusy(DriveTimeMinutes._45) ? <Spinner /> : "45 min" },
            { value: DriveTimeMinutes._60, label: isBusy(DriveTimeMinutes._60) ? <Spinner /> : "60 min" },
        ];
    }), []).get();
    useConditionallyClosable(ClosableOrder.Medium, () => driveTimeStore.clear(), !!driveTimeStore.coordinates);

    if (!driveTimeStore.coordinates)
        return null;

    return (
        <div className={`${isHalfScreenMap ? commonStyles.halfWidthOnMapPanel : commonStyles.onMapPanel} ${styles.panel}`}>
            <div className={styles.row}>
                <SegmentedButtons
                    value={driveTimeStore.minutes}
                    onChange={value => driveTimeStore.setMinutes(value)}
                    options={driveTimeSegmentedButtons}
                />
                <CloseIcon className={commonStyles.clickableIcon} onClick={() => driveTimeStore.clear()} />
            </div>
            <IndustrialScarcityRow />
        </div>
    );
}

const IndustrialScarcityRow = observer(() => {
    const driveTimeStore = useDriveTimeStore();

    if (driveTimeStore.isLoading || driveTimeStore.isIndustrialScarcityLoading)
        return null;

    if (!driveTimeStore.isIndustrialScarcitySupported)
        return (
            <div className={styles.scarcity}>
                <span>Industrial:</span>
                <span className={styles.scarcityValue}>N/A</span>
            </div>
        );

    if (driveTimeStore.industrialScarcityPercent == null)
        return (
            <div className={`${styles.scarcity} ${styles.scarcityEmpty}`}>
                Industrial share unavailable
            </div>
        );

    return (
        <div className={styles.scarcity}>
            <span>Industrial:</span>
            <span className={styles.scarcityValue}>{driveTimeStore.industrialScarcityPercent.toFixed(1)}%</span>
        </div>
    );
});

export default observer(DriveTime);